import React, { useState, useEffect } from 'react';
import { reportesApi } from '../services/api';
import { useSucursal } from '../context/SucursalContext';

const Reportes = () => {
  const { getSucursalId, getSucursalNombre } = useSucursal();
  const [activeTab, setActiveTab] = useState('resumen');
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Data states
  const [resumen, setResumen] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [ingresosMensuales, setIngresosMensuales] = useState([]);
  const [pendientesCobro, setPendientesCobro] = useState({ data: [], total_pendiente: 0 });
  const [comparativo, setComparativo] = useState([]);
  const [clientesFrecuentes, setClientesFrecuentes] = useState([]);

  useEffect(() => {
    loadReportData();
  }, [activeTab, getSucursalId()]);

  const loadReportData = async () => {
    setLoading(true);
    const tienda_id = getSucursalId();
    const params = { tienda_id };
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;

    try {
      switch (activeTab) {
        case 'resumen':
          const resumenData = await reportesApi.getResumen(params);
          setResumen(resumenData);
          break;
        case 'servicios':
          const serviciosData = await reportesApi.getPorServicios(params);
          setServicios(serviciosData.data || []);
          break;
        case 'tecnicos':
          const tecnicosData = await reportesApi.getPorTecnicos(params);
          setTecnicos(tecnicosData.data || []);
          break;
        case 'ingresos':
          const ingresosData = await reportesApi.getIngresosMensuales(params);
          setIngresosMensuales(ingresosData.data || []);
          break;
        case 'pendientes':
          const pendientesData = await reportesApi.getPendientesCobro(params);
          setPendientesCobro(pendientesData);
          break;
        case 'sucursales':
          const comparativoData = await reportesApi.getComparativoSucursales(params);
          setComparativo(comparativoData.data || []);
          break;
        case 'clientes':
          const clientesData = await reportesApi.getClientesFrecuentes(params);
          setClientesFrecuentes(clientesData.data || []);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error cargando reporte:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadReportData();
  };

  const formatMoney = (amount) => `Q${parseFloat(amount || 0).toFixed(2)}`;
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('es-GT') : '-';

  const tabs = [
    { id: 'resumen', label: 'Resumen General' },
    { id: 'servicios', label: 'Por Servicios' },
    { id: 'tecnicos', label: 'Por Técnicos' },
    { id: 'ingresos', label: 'Ingresos Mensuales' },
    { id: 'pendientes', label: 'Pendientes de Cobro' },
    { id: 'clientes', label: 'Clientes Frecuentes' },
    { id: 'sucursales', label: 'Comparativo Sucursales' },
  ];

  const getMaxValue = (data, key) => Math.max(...data.map(d => parseFloat(d[key]) || 0), 1);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reportes</h1>
          <p className="page-subtitle">{getSucursalNombre()}</p>
        </div>
        <button onClick={() => window.print()} className="btn btn-secondary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <polyline points="6 9 6 2 18 2 18 9"/>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
            <rect x="6" y="14" width="12" height="8"/>
          </svg>
          Imprimir
        </button>
      </div>

      {/* Filtros */}
      <div className="report-filters">
        <div className="form-group">
          <label className="form-label">Desde</label>
          <input
            type="date"
            className="form-control"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Hasta</label>
          <input
            type="date"
            className="form-control"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button onClick={handleFilter} className="btn btn-primary">
            Aplicar Filtros
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="report-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`report-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          {/* Resumen General */}
          {activeTab === 'resumen' && resumen && (
            <div>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon blue">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{resumen.totales?.total_ordenes || 0}</h3>
                    <p>Total Órdenes</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon green">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="1" x2="12" y2="23"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(resumen.totales?.total_ingresos)}</h3>
                    <p>Total Ingresos</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon orange">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(resumen.totales?.total_pendiente)}</h3>
                    <p>Pendiente de Cobro</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon blue">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="20" x2="18" y2="10"/>
                      <line x1="12" y1="20" x2="12" y2="4"/>
                      <line x1="6" y1="20" x2="6" y2="14"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(resumen.totales?.ticket_promedio)}</h3>
                    <p>Ticket Promedio</p>
                  </div>
                </div>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-info">
                    <h3>{resumen.hoy?.cantidad || 0}</h3>
                    <p>Órdenes Hoy ({formatMoney(resumen.hoy?.monto)})</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3>{resumen.semana?.cantidad || 0}</h3>
                    <p>Esta Semana ({formatMoney(resumen.semana?.monto)})</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-info">
                    <h3>{resumen.mes?.cantidad || 0}</h3>
                    <p>Este Mes ({formatMoney(resumen.mes?.monto)})</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Por Estado</h3>
                <div className="bar-chart">
                  {resumen.por_status?.map(item => {
                    const maxMonto = getMaxValue(resumen.por_status, 'monto');
                    const width = (parseFloat(item.monto) / maxMonto) * 100;
                    return (
                      <div key={item.status} className="bar-item">
                        <span className="bar-label" style={{ textTransform: 'capitalize' }}>{item.status}</span>
                        <div className="bar-container">
                          <div
                            className={`bar-fill ${item.status === 'entregada' ? 'green' : item.status === 'terminada' ? 'blue' : 'orange'}`}
                            style={{ width: `${width}%` }}
                          />
                        </div>
                        <span className="bar-value">{item.cantidad} ({formatMoney(item.monto)})</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Por Servicios */}
          {activeTab === 'servicios' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Órdenes por Tipo de Servicio</h3>
              {servicios.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="bar-chart">
                  {servicios.map(item => {
                    const maxCantidad = getMaxValue(servicios, 'cantidad');
                    const width = (parseInt(item.cantidad) / maxCantidad) * 100;
                    return (
                      <div key={item.tipo_servicio} className="bar-item">
                        <span className="bar-label">{item.tipo_servicio}</span>
                        <div className="bar-container">
                          <div className="bar-fill blue" style={{ width: `${width}%` }} />
                        </div>
                        <span className="bar-value">{item.cantidad} - {formatMoney(item.ingresos)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Por Técnicos */}
          {activeTab === 'tecnicos' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Rendimiento por Técnico</h3>
              {tecnicos.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Técnico</th>
                        <th>Órdenes</th>
                        <th>Completadas</th>
                        <th>Ingresos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tecnicos.map((tec, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{tec.tecnico || 'Sin asignar'}</td>
                          <td>{tec.cantidad}</td>
                          <td>
                            <span className="status-badge status-terminada">
                              {tec.completadas}
                            </span>
                          </td>
                          <td style={{ fontWeight: 500 }}>{formatMoney(tec.ingresos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Ingresos Mensuales */}
          {activeTab === 'ingresos' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Ingresos Mensuales (Últimos 12 meses)</h3>
              {ingresosMensuales.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="bar-chart">
                  {ingresosMensuales.map(item => {
                    const maxIngresos = getMaxValue(ingresosMensuales, 'ingresos');
                    const width = (parseFloat(item.ingresos) / maxIngresos) * 100;
                    return (
                      <div key={item.mes} className="bar-item">
                        <span className="bar-label">{item.mes}</span>
                        <div className="bar-container">
                          <div className="bar-fill green" style={{ width: `${width}%` }} />
                        </div>
                        <span className="bar-value">{item.cantidad} - {formatMoney(item.ingresos)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Pendientes de Cobro */}
          {activeTab === 'pendientes' && (
            <div>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon red">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  </div>
                  <div className="stat-info">
                    <h3>{formatMoney(pendientesCobro.total_pendiente)}</h3>
                    <p>Total Pendiente ({pendientesCobro.cantidad || 0} órdenes)</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="card-title" style={{ marginBottom: '20px' }}>Detalle de Pendientes</h3>
                {pendientesCobro.data?.length === 0 ? (
                  <p style={{ color: 'var(--text-light)' }}>No hay pagos pendientes</p>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>N° Orden</th>
                          <th>Cliente</th>
                          <th>Teléfono</th>
                          <th>Fecha</th>
                          <th>Total</th>
                          <th>Anticipo</th>
                          <th>Saldo</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendientesCobro.data?.map(orden => (
                          <tr key={orden.numero_orden}>
                            <td style={{ fontWeight: 600 }}>#{orden.numero_orden}</td>
                            <td>{orden.nombre_cliente}</td>
                            <td>{orden.telefono_cliente || '-'}</td>
                            <td>{formatDate(orden.fecha_recepcion)}</td>
                            <td>{formatMoney(orden.valor_total)}</td>
                            <td>{formatMoney(orden.anticipo)}</td>
                            <td style={{ fontWeight: 600, color: 'var(--danger-color)' }}>
                              {formatMoney(orden.saldo)}
                            </td>
                            <td>
                              <span className={`status-badge status-${orden.status}`}>
                                {orden.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Clientes Frecuentes */}
          {activeTab === 'clientes' && (
            <div className="card">
              <h3 className="card-title" style={{ marginBottom: '20px' }}>Clientes Frecuentes</h3>
              {clientesFrecuentes.length === 0 ? (
                <p style={{ color: 'var(--text-light)' }}>No hay datos para mostrar</p>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Teléfono</th>
                        <th>Total Órdenes</th>
                        <th>Total Gastado</th>
                        <th>Última Visita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientesFrecuentes.map((cliente, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{cliente.nombre_cliente}</td>
                          <td>{cliente.telefono_cliente || '-'}</td>
                          <td>
                            <span className="status-badge status-proceso">
                              {cliente.total_ordenes}
                            </span>
                          </td>
                          <td style={{ fontWeight: 500 }}>{formatMoney(cliente.total_gastado)}</td>
                          <td>{formatDate(cliente.ultima_visita)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Comparativo Sucursales */}
          {activeTab === 'sucursales' && (
            <div>
              <div className="stats-grid">
                {comparativo.map(suc => (
                  <div key={suc.tienda_id} className="sucursal-card">
                    <div className="sucursal-card-header">
                      <h4 className="sucursal-card-title">{suc.tienda}</h4>
                    </div>
                    <div className="sucursal-stats">
                      <div className="sucursal-stat">
                        <div className="sucursal-stat-value">{suc.total_ordenes}</div>
                        <div className="sucursal-stat-label">Órdenes</div>
                      </div>
                      <div className="sucursal-stat">
                        <div className="sucursal-stat-value" style={{ fontSize: '18px' }}>
                          {formatMoney(suc.total_ingresos)}
                        </div>
                        <div className="sucursal-stat-label">Ingresos</div>
                      </div>
                      <div className="sucursal-stat">
                        <div className="sucursal-stat-value" style={{ color: 'var(--danger-color)', fontSize: '18px' }}>
                          {formatMoney(suc.total_pendiente)}
                        </div>
                        <div className="sucursal-stat-label">Pendiente</div>
                      </div>
                    </div>
                    <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <span className="status-badge status-pendiente">{suc.pendientes} Pend.</span>
                      <span className="status-badge status-proceso">{suc.en_proceso} Proc.</span>
                      <span className="status-badge status-terminada">{suc.terminadas} Term.</span>
                      <span className="status-badge status-entregada">{suc.entregadas} Entr.</span>
                    </div>
                  </div>
                ))}
              </div>

              {comparativo.length > 0 && (
                <div className="card">
                  <h3 className="card-title" style={{ marginBottom: '20px' }}>Comparativo de Ingresos</h3>
                  <div className="bar-chart">
                    {comparativo.map(suc => {
                      const maxIngresos = getMaxValue(comparativo, 'total_ingresos');
                      const width = (parseFloat(suc.total_ingresos) / maxIngresos) * 100;
                      return (
                        <div key={suc.tienda_id} className="bar-item">
                          <span className="bar-label">{suc.tienda}</span>
                          <div className="bar-container">
                            <div className="bar-fill green" style={{ width: `${width}%` }} />
                          </div>
                          <span className="bar-value">{formatMoney(suc.total_ingresos)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reportes;
